/**
 * gameRealtime.ts — Game-room realtime session (task G04).
 *
 * Wraps ONE Socket.io connection for ONE open game:
 *  - subscribes to the `game:<id>` room (the server verifies membership and
 *    acks with the locked shape — task G02),
 *  - validates every `game:changed` against the locked shared contract and
 *    forwards only events for THIS game,
 *  - re-subscribes automatically on socket (re)connect — rooms do not
 *    survive a reconnect,
 *  - unsubscribes (best effort) and disconnects on dispose (page unmount or
 *    game switch).
 *
 * Framework-free (no React, no DOM, no socket.io import): the React binding
 * lives in useGameRealtime.ts, and the class can be driven in Node tests
 * against the real server.
 */
import {
  gameChangedPayloadSchema,
  gameSubscribeAckSchema,
  socketEvents,
  type GameChangedPayload,
  type GameSubscribeAck,
} from '@shared';
import type { RealtimeSocket } from './socket';

/** Live-update status of one open game. */
export type GameRealtimeStatus = 'connecting' | 'live' | 'forbidden' | 'unavailable';

export interface GameRealtimeOptions {
  /** Called for every contract-valid `game:changed` event for this game. */
  onGameChanged: (payload: GameChangedPayload) => void;
  /** Called whenever the status changes. */
  onStatusChange?: (status: GameRealtimeStatus) => void;
}

const FORBIDDEN_ACK: GameSubscribeAck = { ok: false, error: 'FORBIDDEN' };

/** Upper bound for the unsubscribe acknowledgement before we disconnect anyway. */
const UNSUBSCRIBE_ACK_TIMEOUT_MS = 2000;

export class GameRealtimeSession {
  private status: GameRealtimeStatus = 'connecting';
  /** True once a room subscription was requested — (re)connects must re-acquire the room. */
  private subscribedOnce = false;
  /** Set by dispose(); afterwards no emits or state changes happen. */
  disposed = false;

  constructor(
    private readonly socket: RealtimeSocket,
    private readonly gameId: number,
    private readonly options: GameRealtimeOptions,
  ) {
    this.socket.on(socketEvents.server.gameChanged, this.handleGameChanged);
    // Rooms are per-connection: re-acquire on every (re)connect.
    this.socket.on('connect', this.handleConnect);
    this.socket.on('connect_error', this.handleConnectError);
  }

  getStatus(): GameRealtimeStatus {
    return this.status;
  }

  /** Marks the channel unavailable (owner calls this when connect/subscribe fails). */
  markUnavailable(): void {
    this.setStatus('unavailable');
  }

  private setStatus(next: GameRealtimeStatus): void {
    if (this.disposed || this.status === next) return;
    this.status = next;
    this.options.onStatusChange?.(next);
  }

  private handleGameChanged = (raw: unknown): void => {
    if (this.disposed) return;
    // The wire payload is validated against the locked shared contract;
    // anything off-contract is dropped (defense in depth, feeds G05).
    const parsed = gameChangedPayloadSchema.safeParse(raw);
    if (!parsed.success) return;
    // Only events for THIS game are forwarded to the page.
    if (parsed.data.gameId !== this.gameId) return;
    this.options.onGameChanged(parsed.data);
  };

  private handleConnect = (): void => {
    if (this.disposed) return;
    if (this.subscribedOnce) void this.subscribe();
  };

  private handleConnectError = (): void => {
    if (this.disposed) return;
    // Live updates were lost (or never established). Socket.io keeps
    // auto-reconnecting in the background; handleConnect re-subscribes on
    // the next successful connect and restores the 'live' status.
    if (this.subscribedOnce) this.setStatus('unavailable');
  };

  /**
   * Resolves once the socket is connected; rejects on handshake failure
   * (e.g. UNAUTHENTICATED — the session cookie was missing/expired).
   */
  connect(): Promise<void> {
    if (this.disposed) return Promise.reject(new Error('realtime session disposed'));
    if (this.socket.connected) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      const onConnect = (): void => {
        cleanup();
        resolve();
      };
      const onError = (err: unknown): void => {
        cleanup();
        reject(err instanceof Error ? err : new Error('socket connection failed'));
      };
      const cleanup = (): void => {
        this.socket.off('connect', onConnect);
        this.socket.off('connect_error', onError);
      };
      this.socket.once('connect', onConnect);
      this.socket.once('connect_error', onError);
    });
  }

  /**
   * Joins the `game:<id>` room. The server checks membership itself and
   * acks with the locked shape `{ ok: true }` or
   * `{ ok: false, error: 'FORBIDDEN' }`.
   */
  subscribe(): Promise<GameSubscribeAck> {
    // From now on, any (re)connect must re-acquire the room.
    this.subscribedOnce = true;
    return new Promise((resolve) => {
      if (this.disposed) {
        resolve(FORBIDDEN_ACK);
        return;
      }
      this.socket.emit(
        socketEvents.client.gameSubscribe,
        { gameId: this.gameId },
        (rawAck: unknown) => {
          if (this.disposed) {
            resolve(FORBIDDEN_ACK);
            return;
          }
          const parsed = gameSubscribeAckSchema.safeParse(rawAck);
          const ack: GameSubscribeAck = parsed.success ? parsed.data : FORBIDDEN_ACK;
          this.setStatus(ack.ok ? 'live' : 'forbidden');
          resolve(ack);
        },
      );
    });
  }

  /** Leaves the room (best effort) and disconnects the socket. Idempotent. */
  dispose(): Promise<void> {
    if (this.disposed) return Promise.resolve();
    this.disposed = true;
    this.socket.off(socketEvents.server.gameChanged, this.handleGameChanged);
    this.socket.off('connect', this.handleConnect);
    this.socket.off('connect_error', this.handleConnectError);

    const unsubscribe: Promise<void> = this.socket.connected
      ? new Promise((resolve) => {
          let settled = false;
          const finish = (): void => {
            if (settled) return;
            settled = true;
            resolve();
          };
          const timer: ReturnType<typeof setTimeout> = setTimeout(finish, UNSUBSCRIBE_ACK_TIMEOUT_MS);
          this.socket.emit(
            socketEvents.client.gameUnsubscribe,
            { gameId: this.gameId },
            () => {
              clearTimeout(timer);
              finish();
            },
          );
        })
      : Promise.resolve();

    return unsubscribe.then(() => {
      this.socket.disconnect();
    });
  }
}
